import { useMemo, useState } from 'react'
import {
  type SchoolSupplierIdentifier,
  isCurrentlyValid,
} from './types'
import { useLieferanten } from '../../verwaltung/lieferanten/api/lieferantenApi'
import { TextInput, Dropdown, type DropdownOption } from '../../../shared/ui'

type SortKey = 'pz' | 'valid_from' | 'supplier'
type SortDir = 'asc' | 'desc'

interface SchoolIdentifiersPanelProps {
  schoolId: string
  identifiers: SchoolSupplierIdentifier[]
  onAdd?: (lieferantId: string) => void
  onEdit?: (id: SchoolSupplierIdentifier) => void
  prefillCustomerNumber?: string
  prefillLieferantId?: string
  /**
   * Hides the panel's own h3 + subtitle (used when the host already provides
   * a section title above the panel — avoids duplicate headers).
   * The "Historie anzeigen" toggle is preserved either way.
   */
  hideHeader?: boolean
}

// Reusable panel showing all of a school's Lieferanten-Kennungen, grouped by supplier.
// Only SCD2-suppliers appear here (isSCD2 === true); flat fields (PCA Schulcode,
// DATEV Personalkostenstelle) are shown in the Stammdaten tab instead.
// SCD Type 2: shows current + historical entries per supplier.

export default function SchoolIdentifiersPanel({
  schoolId,
  identifiers,
  onAdd,
  onEdit,
  prefillCustomerNumber: _prefillCustomerNumber,
  prefillLieferantId: _prefillLieferantId,
  hideHeader = false,
}: SchoolIdentifiersPanelProps) {
  const [showHistory, setShowHistory] = useState(false)
  const [search,      setSearch]      = useState('')
  const [supplierFilter, setSupplierFilter] = useState<string>('')
  const [sortKey,     setSortKey]     = useState<SortKey>('valid_from')
  const [sortDir,     setSortDir]     = useState<SortDir>('desc')
  const today = new Date().toISOString().slice(0, 10)

  const { data: allLieferanten = [] } = useLieferanten()

  // Only suppliers that need SCD2 history tracking appear in this panel
  const scd2Lieferanten = useMemo(
    () => allLieferanten.filter((l) => l.isSCD2 && l.active),
    [allLieferanten],
  )

  // Group identifiers by lieferantId for this school, applying filters/sort
  const grouped = useMemo(() => {
    const map = new Map<string, SchoolSupplierIdentifier[]>()
    const q = search.trim().toLowerCase()
    for (const id of identifiers) {
      if (id.schoolId !== schoolId) continue
      if (supplierFilter && id.lieferantId !== supplierFilter) continue
      if (q && !id.customerNumber.toLowerCase().includes(q) && !(id.notes ?? '').toLowerCase().includes(q)) continue
      const list = map.get(id.lieferantId) ?? []
      list.push(id)
      map.set(id.lieferantId, list)
    }
    // Sort each group by selected key+dir; current rows always float to top
    const sortFn = (a: SchoolSupplierIdentifier, b: SchoolSupplierIdentifier) => {
      const aActive = isCurrentlyValid(a, today) ? 0 : 1
      const bActive = isCurrentlyValid(b, today) ? 0 : 1
      if (aActive !== bActive) return aActive - bActive
      let cmp = 0
      if (sortKey === 'pz')           cmp = a.customerNumber.localeCompare(b.customerNumber, 'de', { numeric: true })
      else if (sortKey === 'valid_from') cmp = a.validFrom.localeCompare(b.validFrom)
      else                             cmp = a.lieferantId.localeCompare(b.lieferantId)
      return sortDir === 'asc' ? cmp : -cmp
    }
    for (const [k, list] of map) {
      list.sort(sortFn)
      map.set(k, list)
    }
    return map
  }, [identifiers, schoolId, search, supplierFilter, sortKey, sortDir, today])

  // Any lieferantId in the data that is NOT in the scd2Lieferanten list
  // (e.g. legacy entries from before the Lieferanten module existed)
  const knownIds = new Set(scd2Lieferanten.map((l) => l.id))
  const extraIds = Array.from(grouped.keys()).filter((id) => !knownIds.has(id))

  // Every supplier that has ANY identifier for this school (computed from the
  // UNFILTERED set so the dropdown doesn't shrink when a filter is active).
  const presentIds = useMemo(() => {
    const s = new Set<string>()
    for (const id of identifiers) if (id.schoolId === schoolId) s.add(id.lieferantId)
    return s
  }, [identifiers, schoolId])

  // Filter options = SCD2 suppliers ∪ every supplier actually present in the data
  // (e.g. boettjer/refood, which aren't SCD2-flagged but do have Kennungen here).
  const supplierOptions: DropdownOption[] = useMemo(() => {
    const opts: DropdownOption[] = [{ value: '', label: 'Alle Lieferanten' }]
    const seen = new Set<string>()
    const add = (id: string, label: string) => { if (!seen.has(id)) { opts.push({ value: id, label }); seen.add(id) } }
    for (const l of scd2Lieferanten) add(l.id, l.name)
    for (const id of presentIds) add(id, allLieferanten.find(l => l.id === id)?.name ?? id)
    return opts
  }, [scd2Lieferanten, presentIds, allLieferanten])

  const sortOptions: DropdownOption[] = [
    { value: 'valid_from:desc', label: 'Datum (neueste zuerst)' },
    { value: 'valid_from:asc',  label: 'Datum (älteste zuerst)' },
    { value: 'pz:asc',          label: 'PZ aufsteigend' },
    { value: 'pz:desc',         label: 'PZ absteigend' },
    { value: 'supplier:asc',    label: 'Lieferant A→Z' },
  ]
  const sortValue = `${sortKey}:${sortDir}`
  const onSortChange = (v: string) => {
    const [k, d] = v.split(':') as [SortKey, SortDir]
    setSortKey(k); setSortDir(d)
  }

  const totalActive   = Array.from(grouped.values()).flat().filter(i => isCurrentlyValid(i, today)).length
  const totalAll      = Array.from(grouped.values()).flat().length

  return (
    <div className="space-y-4">
      <div className={`flex items-start ${hideHeader ? 'justify-end' : 'justify-between'} gap-4 flex-wrap`}>
        {!hideHeader && (
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-on-surface">Lieferanten-Kennungen</h3>
            <p className="text-xs text-on-surface-variant mt-0.5">
              Customer-IDs und Kd.PZ pro Lieferant — mit Zeitverlauf (SCD Type 2)
              <span className="ml-2 text-on-surface-variant/70 tabular-nums">· {totalActive} aktiv / {totalAll} gesamt</span>
            </p>
          </div>
        )}
        <button
          type="button"
          onClick={() => setShowHistory((v) => !v)}
          className="text-xs text-primary hover:underline inline-flex items-center gap-1 shrink-0"
        >
          <span className="material-symbols-outlined text-base">{showHistory ? 'visibility_off' : 'history'}</span>
          {showHistory ? 'Historie ausblenden' : 'Historie anzeigen'}
        </button>
      </div>

      {/* Toolbar: search + supplier filter + sort */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_220px_220px] gap-2 items-end">
        <TextInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="PZ-Code oder Notizen suchen…"
        />
        <Dropdown
          value={supplierFilter}
          onChange={setSupplierFilter}
          options={supplierOptions}
          placeholder="Alle Lieferanten"
        />
        <Dropdown
          value={sortValue}
          onChange={onSortChange}
          options={sortOptions}
          placeholder="Sortierung"
        />
      </div>

      <div className="space-y-3">
        {[...scd2Lieferanten.map((l) => l.id), ...extraIds].map((lieferantId) => {
          const lieferant = scd2Lieferanten.find((l) => l.id === lieferantId)
          const list = grouped.get(lieferantId) ?? []
          // A school can legitimately have MULTIPLE active customer numbers
          // for the same supplier (e.g. multiple delivery sites). Render all.
          const currents   = list.filter((i) =>  isCurrentlyValid(i, today))
          const historical = list.filter((i) => !isCurrentlyValid(i, today))

          // Fallback display for unknown/legacy lieferantIds
          const displayLabel = lieferant?.name ?? lieferantId
          const shortLabel = lieferant?.shortLabel ?? lieferantId
          const identifierLabel = lieferant?.identifierLabel ?? 'Kennung'
          const icon = 'inventory_2'

          return (
            <div
              key={lieferantId}
              className="rounded-xl border border-outline-variant/40 bg-surface-container-lowest overflow-hidden"
            >
              <div className="px-4 py-2.5 bg-surface-container-low/50 border-b border-outline-variant/20 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-on-surface-variant text-base">
                    {icon}
                  </span>
                  <span className="text-sm font-semibold text-on-surface">{displayLabel}</span>
                  <span className="text-[11px] text-on-surface-variant">· {identifierLabel}</span>
                  {shortLabel !== displayLabel && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-surface-container text-on-surface-variant border border-outline-variant/30">
                      {shortLabel}
                    </span>
                  )}
                </div>
                {onAdd && (
                  <button
                    type="button"
                    onClick={() => onAdd(lieferantId)}
                    className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                  >
                    <span className="material-symbols-outlined text-base">add</span>
                    Neu
                  </button>
                )}
              </div>

              <div className="divide-y divide-outline-variant/20">
                {/* Column header for the rows below */}
                {(currents.length > 0 || (showHistory && historical.length > 0)) && (
                  <div className="grid grid-cols-[160px_1fr_2fr_24px] gap-3 px-4 py-2 text-[10px] font-semibold uppercase tracking-widest text-on-surface-variant bg-surface-container-low/30">
                    <span>PZ-Code</span>
                    <span>Gültigkeit</span>
                    <span>Notizen / Quelle</span>
                    <span aria-hidden="true" />
                  </div>
                )}

                {/* Current — multiple allowed (e.g. multiple delivery sites) */}
                {currents.length > 0 ? (
                  currents.map((cur) => (
                    <button
                      type="button"
                      key={cur.id}
                      onClick={() => onEdit?.(cur)}
                      className="w-full text-left px-4 py-3 hover:bg-surface-container-low transition grid grid-cols-[160px_1fr_2fr_24px] gap-3 items-center"
                    >
                      <span className="font-mono text-sm font-semibold text-on-surface tabular-nums truncate">{cur.customerNumber}</span>
                      <span className="text-[11px] inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-tertiary-container/30 text-on-tertiary-container border border-tertiary/20 w-fit tabular-nums">
                        <span className="material-symbols-outlined !text-[12px]">check_circle</span>
                        ab {cur.validFrom}
                      </span>
                      <span className="text-[11px] text-on-surface-variant truncate">
                        {cur.notes ?? <span className="italic text-on-surface-variant/60">—</span>}
                        {cur.source && <span className="ml-2 text-[10px] uppercase tracking-wider text-on-surface-variant/70">· {cur.source}</span>}
                      </span>
                      <span className="material-symbols-outlined !text-[16px] text-on-surface-variant/60 justify-self-end">edit</span>
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-3 text-xs text-on-surface-variant italic">
                    Keine aktuelle Kennung — über „Neu" hinzufügen
                  </div>
                )}

                {/* History */}
                {showHistory && historical.map((h) => (
                  <button
                    type="button"
                    key={h.id}
                    onClick={() => onEdit?.(h)}
                    className="w-full text-left px-4 py-2.5 hover:bg-surface-container-low transition grid grid-cols-[160px_1fr_2fr_24px] gap-3 items-center opacity-70 hover:opacity-100"
                  >
                    <span className="font-mono text-xs font-medium text-on-surface-variant tabular-nums truncate">{h.customerNumber}</span>
                    <span className="text-[11px] inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-surface-container text-on-surface-variant border border-outline-variant/40 w-fit tabular-nums">
                      {h.validFrom} → {h.validTo}
                    </span>
                    <span className="text-[11px] text-on-surface-variant truncate italic">
                      {h.notes ?? <span className="opacity-60">—</span>}
                      {h.source && <span className="ml-2 text-[10px] uppercase tracking-wider not-italic text-on-surface-variant/70">· {h.source}</span>}
                    </span>
                    <span className="material-symbols-outlined !text-[16px] text-on-surface-variant/40 justify-self-end">visibility</span>
                  </button>
                ))}

                {showHistory && historical.length === 0 && currents.length > 0 && (
                  <div className="px-4 py-2 text-[11px] text-on-surface-variant italic">
                    Keine historischen Einträge
                  </div>
                )}
              </div>
            </div>
          )
        })}

        {scd2Lieferanten.length === 0 && extraIds.length === 0 && (
          <div className="rounded-xl border border-outline-variant/30 bg-surface-container-low/50 p-4 text-sm text-on-surface-variant text-center">
            Keine SCD2-Lieferanten konfiguriert
          </div>
        )}
      </div>

      <div className="rounded-xl border border-outline-variant/30 bg-surface-container-low/50 p-3 text-xs text-on-surface-variant flex items-start gap-2">
        <span className="material-symbols-outlined text-base text-primary mt-0.5">info</span>
        <p>
          Kennungen werden <strong>nie überschrieben</strong> — jede Änderung erstellt einen neuen Eintrag mit Gültigkeitsdatum.
          So bleiben historische Buchungen nachvollziehbar (SCD Type 2).
        </p>
      </div>
    </div>
  )
}
